"use client";

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { truncateDecimals } from "../utils/numberFormatter";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import QuoteDetails from "./BuyQuoteWallet";
import { getQuote } from "../services/digitalWalletService";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { useAuth } from "../context/AuthContext";
import AssetSelectorWallet from "./AssetSelectorWallet";
import StatusStep from "./ui/status-step";
import { useQueryClient } from "@tanstack/react-query";
import { useAvailableBalances } from "../hooks/query/queries";
import { useFeatureAccess } from "./FeatureGate";
import { refreshBalanceQueries } from "../services/invalidator";
import { SecuredFooter } from "./ui/secured-footer";
import CardSpinningLoader from "./CardSpinningLoader";

interface Asset {
  assetId: string;
  asset: string;
  [key: string]: any;
}

interface TradingPair {
  id: string;
  pair: string;
  asset: string;
  assetIcon?: string;
  assetName?: string;
  type?: string;
  rate?: number;
  postTradeEnabled?: boolean;
  availableAmount?: number;
  formattedAvailableAmount?: string;
  [key: string]: any;
}

interface Quote {
  incomingAmount?: number;
  outgoingAmount?: number;
  serviceFee?: number;
  feePercentage?: {
    $numberDecimal?: string;
  };
  tradeType?: string;
  pair?: string;
  userId?: string;
  rate?: number;
  assetId?: string;
  quoteId?: string;
  createdAt?: number;
  expiresAt?: number;
  [key: string]: any;
}

interface BuySellPageProps {
  assetToSelect?: any;
  tab?: string;
  quickActionTab?: string;
  childTab?: string;
  tradingAssets?: any;
  pairObject?: Record<string, any>;
  zarBalance: number;
}

const tradeTypes = ["Buy", "Sell"] as const;
type TradeType = (typeof tradeTypes)[number];

type ViewState = "form" | "quote" | "processing" | "success";

export default function BuySellPage({
  assetToSelect,
  tab,
  quickActionTab,
  childTab,
  tradingAssets,
  pairObject,
  zarBalance,
}: BuySellPageProps) {
  const [tradeType, setTradeType] = useState<TradeType | null>(null);
  const [amount, setAmount] = useState<string | number | null>(null);
  const [inputAmount, setinputAmount] = useState<number | null>(null);
  const [orderAmount, setOrderAmount] = useState<number | null>(null);
  const [incomingAmount, setIncomningAmount] = useState<number | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedSaleAsset, setSelectedSaleAsset] = useState<Asset | null>(
    null
  );
  const [selectedPair, setSelectedPair] = useState<TradingPair | null>(null);
  const [selectedSalePair, setSelectedSalePair] = useState<TradingPair | null>(
    null
  );
  const [tradeRate, setTradeRate] = useState<{ rate: number } | null>(null);
  const [quote, setQuote] = useState<Quote>({});
  const [viewState, setViewState] = useState<ViewState>("form");
  const [order, setOrder] = useState<boolean>(false);
  const [isTrading, setIsTrading] = useState<boolean>(false);
  const [tradeResult, setTradeResult] = useState<{
    success: boolean;
    message?: string;
    trade?: any;
  } | null>(null);
  const [isPostTrade, setIsPostTrade] = useState<boolean>(false);
  const [currentPairs, setCurrentPairs] = useState<any>(null);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [isPairPostTradeAllowed, setIsPairPostTradeAllowed] =
    useState<boolean>(false);
  const [isOrderCreationView, setIsOrderCreationView] =
    useState<boolean>(false);
  const [fetchingQuoteAfterOrder, setFetchingQuoteAfterOrder] =
    useState<boolean>(false);
  const isConfirmingRef = useRef<boolean>(false);
  const queryClient = useQueryClient();
  const { userProfile, settings } = useAuth();
  const { toast } = useToast();
  const isPostTradeAllowed = settings?.postTradeSettlements || false;

  useEffect(() => {
    try {
      const storedTradeResult = sessionStorage.getItem("buySellTradeResult");
      const storedViewState = sessionStorage.getItem("buySellViewState");
      const storedTradeType = sessionStorage.getItem("buySellTradeType");

      if (storedTradeResult && storedViewState === "success") {
        const tradeResultData = JSON.parse(storedTradeResult);
        setTradeResult(tradeResultData);
        setViewState("success");

        if (storedTradeType) {
          setTradeType(storedTradeType as TradeType);
        } else if (tradeResultData?.trade?.type) {
          const tradeTypeFromData =
            tradeResultData.trade.type === "buy" ? "Buy" : "Sell";
          setTradeType(tradeTypeFromData);
        }
      }
    } catch (e) {}
  }, []);

  const { data: walletAssets } = useAvailableBalances(userProfile?._id);
  const { flags: featureFlags, isLoading: isLoadingFlags } = useFeatureAccess(
    "mainFeatures",
    ["buyCrypto", "sellCrypto"]
  ) as { flags: Record<string, boolean>; isLoading: boolean };

  useEffect(() => {
    if (isLoadingFlags) return;
    if (tradeType !== null) return;
    const flags = featureFlags as Record<string, boolean>;
    if (flags?.buyCrypto) {
      setTradeType("Buy");
    } else if (flags?.sellCrypto) {
      setTradeType("Sell");
    }
  }, [featureFlags, tradeType, isLoadingFlags]);

  const fiatBalances = useMemo(
    () =>
      walletAssets?.map((asset: Asset) => {
        return {
          asset: asset.asset,
          valueInLocalCurrency:
            asset.asset === "zar"
              ? asset.availableAmountFormatted
              : asset.valueInLocalCurrency,
        };
      }) ?? [],
    [walletAssets]
  );

  const getSuccessConfig = () => {
    const trade = tradeResult?.trade;
    const parseDecimal = (value: any): number => {
      if (typeof value === "number") return value;
      if (typeof value === "string") return parseFloat(value);
      if (value?.$numberDecimal) return parseFloat(value.$numberDecimal);
      return 0;
    };

    const currentTradeType =
      (trade?.type === "buy"
        ? "Buy"
        : trade?.type === "sell"
          ? "Sell"
          : null) ||
      tradeType ||
      "Buy";

    if (currentTradeType === "Buy") {
      const assetAmount = trade
        ? parseDecimal(trade.incomingAmount)
        : incomingAmount || 0;
      const assetSymbol =
        trade?.incomingAsset?.toUpperCase() ||
        selectedAsset?.asset?.toUpperCase() ||
        "CRYPTO";
      const targetPrice =
        orderAmount ||
        (trade ? parseDecimal(trade.price) : tradeRate?.rate || 0);

      if (order) {
        const config = {
          title: "Your buy order has been set",
          description: `You'll buy ${truncateDecimals(assetAmount, 6)} ${assetSymbol} if its price goes down to R ${truncateDecimals(targetPrice, 2)}`,
          buttonText: "Done",
        };
        return config;
      } else {
        const config = {
          title: "Your portfolio just got stronger",
          description: `Your crypto is now in your wallet and ready to go.`,
          buttonText: "Done",
        };
        return config;
      }
    }

    if (currentTradeType === "Sell") {
      const assetAmount = trade
        ? parseDecimal(trade.outgoingAmount)
        : inputAmount || 0;
      const assetSymbol =
        trade?.outgoingAsset?.toUpperCase() ||
        selectedSaleAsset?.asset?.toUpperCase() ||
        "CRYPTO";
      const targetPrice =
        orderAmount ||
        (trade ? parseDecimal(trade.price) : tradeRate?.rate || 0);

      if (order) {
        const config = {
          title: "Your sell order has been set",
          description: `You'll sell ${truncateDecimals(assetAmount, 6)} ${assetSymbol} if its price goes up to R ${truncateDecimals(targetPrice, 2)}`,
          buttonText: "Done",
        };
        return config;
      }

      const config = {
        title: "Cash in Hand.",
        description: (
          <>
            You've freed up funds and kept <br /> control of your next move.
          </>
        ),
        buttonText: "Done",
      };
      return config;
    }

    const config = {
      title: "Transaction Successful",
      description: "Your transaction has been completed.",
      buttonText: "Done",
    };
    return config;
  };

  const getFailureConfig = () => {
    const trade = tradeResult?.trade;
    const currentTradeType =
      tradeType ||
      (trade?.type === "buy"
        ? "Buy"
        : trade?.type === "sell"
          ? "Sell"
          : null) ||
      "Buy";

    if (currentTradeType === "Sell") {
      const config = {
        title: "Transaction failed",
        description:
          "Your crypto sale was not successful. Please try again. If the problem persists, please contact support.",
        buttonText: "Try Again",
      };
      return config;
    }

    const config = {
      title: "Transaction failed",
      description:
        "Your crypto purchase was not successful. Please try again. If the problem persists, please contact support.",
      buttonText: "Try Again",
    };
    return config;
  };

  useEffect(() => {
    const assetId =
      tradeType === "Buy" ? selectedAsset?.assetId : selectedSaleAsset?.assetId;
    if (!assetId || !pairObject) return;

    let currentAssetPairs = undefined;
    if (Object.prototype.hasOwnProperty.call(pairObject, assetId)) {
      const key = String(assetId);
      currentAssetPairs = pairObject[key as keyof typeof pairObject];
    }

    setCurrentPairs(currentAssetPairs);
  }, [selectedAsset, selectedSaleAsset, pairObject, tradeType]);

  useEffect(() => {
    if (viewState === "success") return;
    const validTabs: TradeType[] = ["Buy", "Sell"];
    if (!tab) return;
    if (validTabs.includes(tab as TradeType)) {
      setTradeType(tab as TradeType);
    }
  }, [tab, viewState]);

  useEffect(() => {
    if (viewState === "success") return;
    const validTabs: TradeType[] = ["Buy", "Sell"];
    if (!childTab) return;
    if (validTabs.includes(childTab as TradeType)) {
      setTradeType(childTab as TradeType);
    }
  }, [childTab, viewState]);

  useEffect(() => {
    if (viewState === "success") return;
    const validTabs: TradeType[] = ["Buy", "Sell"];
    if (!quickActionTab) return;
    if (validTabs.includes(quickActionTab as TradeType)) {
      setTradeType(quickActionTab as TradeType);
    }
  }, [quickActionTab, viewState]);

  const handleConfirm = async (result: any): Promise<void> => {
    if (isConfirmingRef.current) {
      return;
    }

    isConfirmingRef.current = true;

    const tradeData =
      result?.trade || result?.data?.trade || result?.data?.data?.trade;
    const responseData = result?.data;

    const isSuccess =
      result?.success === true ||
      (result?.success !== false &&
        result?.success !== undefined &&
        tradeData !== undefined &&
        tradeData !== null);

    const actualTradeData =
      tradeData || responseData?.data?.trade || responseData?.trade;

    const tradeResultData = {
      success: isSuccess,
      message: isSuccess
        ? result?.message ||
          responseData?.message ||
          "Trade completed successfully"
        : result?.message ||
          result?.error ||
          responseData?.message ||
          "Trade failed. Please try again.",
      trade: actualTradeData || null,
    };

    try {
      sessionStorage.setItem(
        "buySellTradeResult",
        JSON.stringify(tradeResultData)
      );
      sessionStorage.setItem("buySellViewState", "success");
      if (tradeType) {
        sessionStorage.setItem("buySellTradeType", tradeType);
      } else if (actualTradeData?.type) {
        const tradeTypeFromData =
          actualTradeData.type === "buy" ? "Buy" : "Sell";
        sessionStorage.setItem("buySellTradeType", tradeTypeFromData);
      }
    } catch (e) {}

    setTradeResult(tradeResultData);
    setViewState("success");
    setQuote({});

    await new Promise<void>((resolve) => {
      setTimeout(() => {
        isConfirmingRef.current = false;
        resolve();
      }, 100);
    });
  };

  const handleDone = async (): Promise<void> => {
    if (isConfirmingRef.current) {
      return;
    }

    try {
      sessionStorage.removeItem("buySellTradeResult");
      sessionStorage.removeItem("buySellViewState");
      sessionStorage.removeItem("buySellTradeType");
    } catch (e) {}

    try {
      await refreshBalanceQueries(queryClient, 0);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["cryptoAssets"],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ["userOrders"],
          exact: false,
        }),
      ]);
    } catch (err) {
      // continue to form even if refresh fails
    }

    setTradeResult(null);
    setViewState("form");
    setQuote({});
    setAmount(null);
    setinputAmount(null);
    setOrderAmount(null);
    setIncomningAmount(null);
    setSelectedAsset(null);
    setSelectedSaleAsset(null);
    setSelectedPair(null);
    setSelectedSalePair(null);
    setTradeRate(null);
    setOrder(false);
    setIsPostTrade(false);
    setCurrentPairs(null);
    setDisabled(false);
    setIsPairPostTradeAllowed(false);
    setIsTrading(false);
    setIsOrderCreationView(false);
    setFetchingQuoteAfterOrder(false);
  };

  const handleBack = (): void => {
    if (viewState === "success" || isConfirmingRef.current) {
      return;
    }
    setViewState("form");
  };

  const handleAssetChange = useCallback((asset: Asset): void => {
    setSelectedAsset(asset);
  }, []);

  const handleAssetSaleChange = useCallback((asset: Asset): void => {
    setSelectedSaleAsset(asset);
  }, []);

  const handlePairChange = useCallback((pair: TradingPair): void => {
    setSelectedPair(pair);
  }, []);

  const handlePairsSaleChange = useCallback((pair: TradingPair): void => {
    setSelectedSalePair(pair);
  }, []);

  const handleInputAmount = useCallback(
    (outAmount: number, inAmount: number): void => {
      setinputAmount(outAmount);
      setIncomningAmount(inAmount);
    },
    []
  );

  const handleInputSaleAmount = useCallback(
    (inAmount: number, outAmount: number): void => {
      setinputAmount(outAmount);
      setIncomningAmount(inAmount);
    },
    []
  );

  const handleOrderBuy = useCallback((targetAmount: number): void => {
    setOrderAmount(targetAmount);
    setOrder(true);
  }, []);

  const handleOrderCancel = useCallback((): void => {
    setOrder(false);
  }, []);

  const handleOrderViewChange = useCallback((isOrderView: boolean): void => {
    setIsOrderCreationView(isOrderView);
  }, []);

  const orderBuyData = {
    userId: userProfile?._id,
    currencyPair: selectedPair?.pair,
    incomingAsset: selectedAsset?.asset,
    outgoingAsset: selectedPair?.asset,
    orderType: "below",
    type: "buy",
    price: tradeRate?.rate,
    targetPrice: orderAmount,
    incomingAmount: incomingAmount,
    outgoingAmount: inputAmount,
    serviceFee: 0,
    brokerPercentage: 0,
    note: "",
    isPostTrade: isPostTrade,
    assetId: selectedAsset?.assetId,
  };
  const orderSellData = {
    userId: userProfile?._id,
    currencyPair: selectedSalePair?.pair,
    incomingAsset: selectedSalePair?.asset,
    outgoingAsset: selectedSaleAsset?.asset,
    orderType: "above",
    type: "sell",
    price: tradeRate?.rate,
    targetPrice: orderAmount,
    incomingAmount: inputAmount,
    outgoingAmount: incomingAmount,
    serviceFee: 0,
    brokerPercentage: 0,
    note: "",
    isPostTrade: isPostTrade,
    assetId: selectedSaleAsset?.assetId,
  };
  const buyData = {
    userId: userProfile?._id,
    tradeType: tradeType?.toLowerCase() ?? "buy",
    pair: selectedPair?.pair,
    incomingAmount: incomingAmount,
    outgoingAmount: inputAmount,
    price: quote?.rate || tradeRate?.rate,
    serviceFee: 0,
    feePercentage: 0,
    incomingAsset: selectedAsset?.asset,
    outgoingAsset: selectedPair?.asset,
    brokerPercentage: 0,
    brokerAmount: 0,
    isPostTrade: isPostTrade,
  };

  const sellData = {
    userId: userProfile?._id,
    tradeType: tradeType?.toLowerCase() ?? "sell",
    pair: selectedSalePair?.pair,
    incomingAmount: inputAmount,
    outgoingAmount: incomingAmount,
    price: quote?.rate || tradeRate?.rate,
    serviceFee: 0,
    feePercentage: 0,
    incomingAsset: selectedSalePair?.asset,
    outgoingAsset: selectedSaleAsset?.asset,
    brokerPercentage: 0,
    brokerAmount: 0,
    isPostTrade: isPostTrade,
  };

  const quoteData = {
    incomingAsset: selectedAsset?.asset,
    outgoingAsset: selectedPair?.asset,
    assetId: selectedAsset?.assetId,
    amount: inputAmount,
    isPostTrade: isPostTrade,
    tradeType: tradeType?.toLowerCase() ?? "buy",
    pair: selectedPair?.pair,
    isCustom: order,
    userId: userProfile?._id,
    price: orderAmount ? parseFloat(String(orderAmount)) : 0,
  };

  const quoteSaleData = {
    incomingAsset: selectedSalePair?.asset,
    outgoingAsset: selectedSaleAsset?.asset,
    assetId: selectedSaleAsset?.assetId,
    amount: incomingAmount,
    isPostTrade: isPostTrade,
    tradeType: tradeType?.toLowerCase() ?? "sell",
    pair: selectedSalePair?.pair,
    isCustom: order,
    userId: userProfile?._id,
    price: orderAmount ? parseFloat(String(orderAmount)) : 0,
  };

  const handleTabChange = (value: string): void => {
    if (viewState === "success") {
      return;
    }

    setAmount(null);
    setinputAmount(null);
    setOrderAmount(null);
    setIncomningAmount(null);
    setSelectedAsset(null);
    setSelectedSaleAsset(null);
    setSelectedPair(null);
    setSelectedSalePair(null);
    setTradeRate(null);
    setQuote({});
    setOrder(false);
    setIsPostTrade(false);
    setDisabled(false);
    setCurrentPairs(null);
    setIsPairPostTradeAllowed(false);
    setIsTrading(false);
    setTradeResult(null);
    setViewState("form");
    setTradeType(value as TradeType);
  };

  const trade = async (): Promise<void> => {
    if (viewState === "success" || tradeResult) {
      return;
    }
    setIsTrading(true);
    try {
      if (tradeType === "Buy") {
        const numericAmount = parseFloat(String(inputAmount ?? 0));
        if (
          !inputAmount ||
          inputAmount === null ||
          isNaN(numericAmount) ||
          numericAmount <= 0
        ) {
          toast({
            description: "Please enter a valid amount",
            variant: "error",
          });
          setIsTrading(false);
          return;
        }

        try {
          const result = await getQuote(quoteData);
          const resultData = result.data.data;
          setQuote(resultData);
          setViewState("quote");
          setIsTrading(false);
        } catch (error) {
          toast({
            description: `${error}`,
            variant: "error",
          });
          setIsTrading(false);
        }
      }
      if (tradeType === "Sell") {
        const numericAmount = parseFloat(String(incomingAmount ?? 0));
        if (
          !incomingAmount ||
          incomingAmount === null ||
          isNaN(numericAmount) ||
          numericAmount <= 0
        ) {
          toast({
            description: "Please enter a valid amount",
            variant: "error",
          });
          setIsTrading(false);
          return;
        }

        try {
          const result = await getQuote(quoteSaleData);
          const resultData = result.data.data;
          setQuote(resultData);
          setViewState("quote");
          setIsTrading(false);
        } catch (error) {
          toast({
            description: `${error}`,
            variant: "error",
          });
          setIsTrading(false);
        }
      }
    } catch (error) {
      setIsTrading(false);
    }
  };

  useEffect(() => {
    if (!selectedPair && !selectedSalePair) return;

    const isAllowed =
      selectedPair?.postTradeEnabled ??
      selectedSalePair?.postTradeEnabled ??
      false;

    setIsPairPostTradeAllowed(isAllowed);
    if (!isAllowed) {
      setIsPostTrade(false);
    }
  }, [selectedPair, selectedSalePair]);

  useEffect(() => {
    if (tradeResult && viewState !== "success") {
      setViewState("success");
    }
  }, [tradeResult, viewState]);

  if (tradeResult && typeof tradeResult === "object" && tradeResult !== null) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border-gray-100 rounded-2xl">
        <Card className="w-full overflow-hidden border-none shadow-none">
          <CardContent className="px-6 py-32">
            <StatusStep
              isSuccess={tradeResult.success}
              onContinue={handleDone}
              successConfig={getSuccessConfig()}
              failureConfig={getFailureConfig()}
              hideCard={true}
            />
          </CardContent>
        </Card>
        <div className="w-full">
          <SecuredFooter />
        </div>
      </div>
    );
  }

  if (isConfirmingRef.current || viewState === "success") {
    return null;
  }

  if (
    viewState === "quote" &&
    quote &&
    Object.keys(quote).length > 0 &&
    !tradeResult &&
    !isConfirmingRef.current
  ) {
    return (
      <QuoteDetails
        onBack={handleBack}
        data={quote}
        tradeData={buyData}
        orderData={orderBuyData}
        assetId={selectedAsset?.assetId}
        saleAssetId={selectedSaleAsset?.assetId}
        rate={tradeRate?.rate}
        orderSaleData={orderSellData}
        tradeSellData={sellData}
        typeOfTrade={tradeType ?? "Buy"}
        isOrder={order}
        onConfirm={handleConfirm}
        isPostTrade={isPostTrade}
        userId={userProfile?._id}
        onRefresh={trade}
        assetIcon={
          tradeType === "Buy"
            ? selectedAsset?.assetIcon
            : selectedSaleAsset?.assetIcon
        }
        assetName={
          tradeType === "Buy"
            ? selectedAsset?.assetName
            : selectedSaleAsset?.assetName
        }
        assetSymbol={
          tradeType === "Buy" ? selectedAsset?.asset : selectedSaleAsset?.asset
        }
      />
    );
  }

  if (isConfirmingRef.current || (viewState as ViewState) === "success") {
    return null;
  }

  if (fetchingQuoteAfterOrder) {
    return (
      <Card className="flex flex-col w-full lg:pt-8 pt-5 !shadow-none bg-white rounded-2xl">
        <CardContent className="flex flex-col items-center justify-center h-[600px]">
          <CardSpinningLoader />
        </CardContent>
        <SecuredFooter />
      </Card>
    );
  }

  return (
    <Card className="flex flex-col w-full lg:pt-8 pt-5 !shadow-none bg-white rounded-2xl">
      {!isOrderCreationView && (
        <div className="lg:px-8 px-5">
          <Tabs onValueChange={handleTabChange} className="mb-8">
            <TabsList
              className={`grid rounded-full bg-transparent border border-[#EBE9EF] border-[1px] h-[64px] px-[8px] ${(featureFlags as Record<string, boolean>)?.buyCrypto && (featureFlags as Record<string, boolean>)?.sellCrypto ? "grid-cols-2" : "grid-cols-1"}`}
            >
              {(featureFlags as Record<string, boolean>)?.buyCrypto && (
                <TabsTrigger
                  value="Buy"
                  className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                    tradeType === "Buy"
                      ? "bg-[#33125F] text-white"
                      : "text-[#6D6584] bg-transparent"
                  }`}
                >
                  Buy
                </TabsTrigger>
              )}
              {(featureFlags as Record<string, boolean>)?.sellCrypto && (
                <TabsTrigger
                  value="Sell"
                  className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                    tradeType === "Sell"
                      ? "bg-[#33125F] text-white"
                      : "text-[#6D6584] bg-transparent"
                  }`}
                >
                  Sell
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        </div>
      )}
      <CardContent className="px-2">
        <div className="lg:px-5 px-2">
          <div className="space-y-2 md:px-6 px-3">
            {(tradeType === "Buy" || tradeType === "Sell") && (
              <AssetSelectorWallet
                zarBalance={zarBalance}
                hotActionAsset={tab === tradeType ? assetToSelect : null}
                onTarget={handleOrderBuy}
                orderAmount={orderAmount}
                onTargetCancel={handleOrderCancel}
                onSelect={
                  tradeType === "Buy"
                    ? handleAssetChange
                    : handleAssetSaleChange
                }
                fiatBalances={fiatBalances}
                setDisabled={setDisabled}
                setSelectedAsset={setSelectedAsset}
                selectedAsset={selectedAsset}
                selectedSaleAsset={selectedSaleAsset}
                onChange={
                  tradeType === "Buy"
                    ? handleInputAmount
                    : handleInputSaleAmount
                }
                setAmount={setAmount}
                amount={amount}
                tradingAssets={tradingAssets}
                pair={tradeType === "Buy" ? selectedPair : selectedSalePair}
                setParentRate={setTradeRate}
                typeOfTrade={tradeType}
                isPostTrade={isPostTrade}
                currentPairs={currentPairs}
                onPairSelect={
                  tradeType === "Buy" ? handlePairChange : handlePairsSaleChange
                }
                onOrderViewChange={handleOrderViewChange}
                onOrderCreatedAndDone={(amount) => {
                  setFetchingQuoteAfterOrder(true);
                  setOrderAmount(amount);
                  setOrder(true);
                  requestAnimationFrame(() => {
                    void trade().finally(() =>
                      setFetchingQuoteAfterOrder(false)
                    );
                  });
                }}
              />
            )}
          </div>

          {!isOrderCreationView && (
            <>
              {isPostTradeAllowed && !order && isPairPostTradeAllowed && (
                <label className="flex items-center space-x-2 cursor-pointer my-3">
                  <input
                    type="checkbox"
                    checked={isPostTrade}
                    onChange={() => setIsPostTrade(!isPostTrade)}
                    className="w-5 h-5 accent-rose-600 cursor-pointer border border-gray-100"
                  />
                  <span className="text-gray-700">
                    {tradeType === "Sell" ? "Sell" : "Buy"} using post-trade
                    settlement
                  </span>
                </label>
              )}
              <div className="w-full flex justify-center">
                <Button
                  onClick={trade}
                  disabled={isTrading || disabled}
                  className={`px-6 py-4 rounded-full font-semibold transition-opacity w-full sm:min-w-[302px] max-w-sm h-[48px] text-sm sm:text-base ${
                    isTrading || disabled
                      ? "bg-[#F2F1F4] text-[#9D96AF] cursor-not-allowed"
                      : "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white hover:opacity-90"
                  }`}
                >
                  {isTrading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
      <SecuredFooter />
    </Card>
  );
}
